import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import DisplayProgressNote from '../../../components/common/DataTable/DisplayProgressNote'
import Button from 'src/components/common/Button/Button'
import Select from 'react-select'
import { getProgressNoteIcon } from '../../../utils/sharedChartFunctions'
import { convertHyphenDateToSlashDate } from '../../../utils/sharedFunctions'
import { GRAY_300, GRAY_400, GRAY_950 } from '../../../scss/colorConsts'
import { getTheme } from '../../../redux/user/userSlice'

const selectStyleDark = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    color: 'var(--reactselect-input-color)',
    borderColor: GRAY_400,
    boxShadow: 'none',
  }),
  option: (styles, { data, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? 'var(--text-glow-color)'
        : isFocused
          ? 'var(--text-glow-color)'
          : 'var(--reactselect-input-bg-color)',
      color: isSelected ? GRAY_950 : isFocused ? GRAY_950 : data?.color,
      cursor: 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  singleValue: (styles, { data }) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
}

const selectStyleLight = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    color: 'var(--reactselect-input-color)',
    boxShadow: 'none',
  }),
  menu: (styles) => ({ ...styles, backgroundColor: 'var(--reactselect-input-bg-color)' }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? 'var(--text-glow-color)'
        : isFocused
          ? 'var(--text-glow-color)'
          : 'var(--reactselect-input-bg-color)',
      color: 'var(--reactselect-input-color)',
      cursor: 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  singleValue: (styles, { data }) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
}

export default function RecentNoteSection() {
  const theme = useSelector(getTheme)
  const userId = useSelector((state) => state?.user?.user?._id)
  const visitDetails = useSelector((state) => state?.progressNote?.visitDetails)
  const allChartHistory = useSelector((state) => state?.progressNote?.chartHistory) || []
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0)

  // if coming back to a saved note, we don't want to show the current note
  // as a recent-note option, so filter it out of the list
  const chartHistory = allChartHistory?.filter(
    (note) => note?.service_date_from !== visitDetails?.service_date_from,
  )

  const chartHistoryOptions = chartHistory?.map((chart, index) => {
    return {
      label: (
        <>
          {getProgressNoteIcon(chart, { fontSize: '', isHistory: true, userId })}
          {convertHyphenDateToSlashDate(chart?.service_date_from) +
            ' - ' +
            chart?.rendering_provider_name}
        </>
      ),
      value: index,
    }
  })

  return (
    <div>
      <div
        className={'prior-notes-header'}
        style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
        }}
      >
        <Button
          compressed
          style={{
            visibility: currentNoteIndex < (chartHistory?.length || 0) - 1 ? 'visible' : 'hidden',
          }}
          onClick={() => {
            if (chartHistory[currentNoteIndex + 1]) {
              setCurrentNoteIndex(currentNoteIndex + 1)
            }
          }}
        >
          Previous
        </Button>

        <Select
          styles={theme === 'light' ? selectStyleLight : selectStyleDark}
          onChange={({ value }) => {
            setCurrentNoteIndex(value)
          }}
          value={chartHistoryOptions.find((option) => option.value === currentNoteIndex)}
          options={chartHistoryOptions}
          isSearchable={false}
        />
        <Button
          compressed
          style={{
            visibility: currentNoteIndex > 0 ? 'visible' : 'hidden',
          }}
          onClick={() => {
            if (chartHistory[currentNoteIndex - 1]) {
              setCurrentNoteIndex(currentNoteIndex - 1)
            }
          }}
        >
          Next
        </Button>
      </div>
      <div style={{ display: 'flex', overflowY: 'scroll', height: '75vh', marginTop: '8px' }}>
        <DisplayProgressNote
          chartData={chartHistory?.[currentNoteIndex]?.chart}
          visitDetails={{
            ...visitDetails,
            service_date_from: chartHistory?.[currentNoteIndex]?.service_date_from,
          }}
        />
      </div>
    </div>
  )
}
